import { useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Loader2, Download, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import KpiStrip from './KpiStrip';
import RepsLeaderboard from './RepsLeaderboard';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';

function fmt(n) { return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
function fmtD(n) { return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const STATUS_MAP = { draft: 'مسودة', sale: 'مؤكد', done: 'منتهي', cancel: 'ملغي' };
const STATUS_COLOR = { draft: 'bg-amber-100 text-amber-700', sale: 'bg-emerald-100 text-emerald-700', done: 'bg-gray-100 text-gray-600', cancel: 'bg-red-100 text-red-600' };
const PIE_COLORS = ['#1E40AF', '#059669', '#F59E0B', '#DC2626', '#8B5CF6', '#EC4899', '#0EA5E9'];

const CHART_TOOLTIP_STYLE = { contentStyle: { background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: '12px', direction: 'rtl' } };

export default function SalesReport({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topMode, setTopMode] = useState('revenue');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const load = useCallback(async (pg = 0) => {
    setLoading(true);
    setError(null);
    const res = await api.get('/reports/sales', { date_from: dateFrom, date_to: dateTo, page: pg, limit: PAGE_SIZE });
    if (res?.success) setData(res.data);
    else setError(res?.error || 'فشل تحميل تقرير المبيعات');
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { setPage(0); load(0); }, [dateFrom, dateTo]);

  const handlePageChange = (pg) => { setPage(pg); load(pg); };

  const exportCSV = () => {
    if (!data?.orders?.length) return;
    const cols = ['رقم الطلب', 'العميل', 'المندوب', 'التاريخ', 'المبلغ', 'الحالة'];
    const rows = data.orders.map(o => [o.name, o.partner, o.user, o.date, o.amount, STATUS_MAP[o.state] || o.state]);
    const csv = [cols.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'sales_report.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (error) return (
    <div className="p-6 text-center space-y-3">
      <p className="text-red-500 font-medium">{error}</p>
      <Button onClick={() => load(0)} variant="outline" size="sm"><RotateCcw className="w-4 h-4 ml-1" /> إعادة المحاولة</Button>
    </div>
  );

  const kpis = data ? [
    { label: 'إجمالي المبيعات', value: fmt(data.kpis.total_sales), suffix: cur(), to: '/orders' },
    { label: 'عدد الطلبات', value: fmt(data.kpis.orders_count || data.kpis.total_orders), suffix: 'طلب', to: '/orders' },
    { label: 'متوسط الطلب', value: fmtD(data.kpis.avg_order || data.kpis.average_order_value), suffix: cur() },
    { label: 'العملاء الفاعلون', value: fmt(data.kpis.unique_customers), suffix: 'عميل', to: '/customers' },
  ] : [];

  const topProducts = topMode === 'revenue' ? (data?.products || []) : (data?.products || []).slice().sort((a, b) => b.qty - a.qty);
  const totalRev = data?.products?.reduce((s, p) => s + p.revenue, 0) || 1;
  const totalPages = data ? Math.ceil((data.pagination?.total || 0) / PAGE_SIZE) : 0;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <KpiStrip kpis={kpis} loading={loading && !data} />

      {/* Sales trend chart */}
      <div className="bg-card border border-border rounded-2xl p-4">
        <h3 className="font-bold text-foreground mb-3">اتجاه المبيعات اليومي</h3>
        {loading && !data ? <div className="h-48 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> :
          data?.chart?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.chart} {...CHART_TOOLTIP_STYLE}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1E40AF" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#1E40AF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={v => (v / 1000) + 'k'} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE.contentStyle} formatter={v => [fmt(v) + ` ${cur()}`, 'مبيعات']} labelFormatter={l => l} />
                <Area type="monotone" dataKey="total" stroke="#1E40AF" fill="url(#salesGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-muted-foreground py-10">لا توجد بيانات</p>
        }
      </div>

      {/* Top Products */}
      <div className="bg-card border border-border rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-foreground">أفضل المنتجات</h3>
          <div className="flex bg-muted rounded-lg p-0.5">
            <button onClick={() => setTopMode('revenue')} className={`px-2 py-1 rounded text-xs font-semibold transition-all ${topMode === 'revenue' ? 'bg-card shadow text-primary' : 'text-muted-foreground'}`}>إيراد</button>
            <button onClick={() => setTopMode('qty')} className={`px-2 py-1 rounded text-xs font-semibold transition-all ${topMode === 'qty' ? 'bg-card shadow text-primary' : 'text-muted-foreground'}`}>كمية</button>
          </div>
        </div>
        {loading && !data ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div> :
          <div className="space-y-2">
            {topProducts.slice(0, 8).map((p, i) => {
              const pct = topMode === 'revenue' ? Math.round((p.revenue / totalRev) * 100) : Math.round((p.qty / (topProducts[0]?.qty || 1)) * 100);
              return (
                <div key={p.id || i} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-4 text-left">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">{p.name}</p>
                    <div className="w-full bg-muted rounded-full h-1.5 mt-0.5">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <span className="text-xs font-bold text-primary whitespace-nowrap">
                    {topMode === 'revenue' ? fmt(p.revenue) + ` ${cur()}` : fmt(p.qty) + ' وح'}
                  </span>
                </div>
              );
            })}
            {!data?.products?.length && <p className="text-center text-muted-foreground py-6 text-sm">لا توجد بيانات</p>}
          </div>
        }
      </div>

      {/* Top Customers + Category Pie */}
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-card border border-border rounded-2xl p-4">
          <h3 className="font-bold text-foreground mb-3">أفضل العملاء</h3>
          {loading && !data ? <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div> :
            <div className="space-y-2">
              {(data?.customers || []).slice(0, 8).map((c, i) => (
                <div key={c.id || i} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-4 text-left">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.count} طلب</p>
                  </div>
                  <span className="text-xs font-bold text-secondary whitespace-nowrap">{fmt(c.total)} {cur()}</span>
                </div>
              ))}
            </div>
          }
        </div>

        {data?.categories?.length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-4">
            <h3 className="font-bold text-foreground mb-3">مبيعات حسب الفئة</h3>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={data.categories} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`} labelLine={false}>
                  {data.categories.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE.contentStyle} formatter={v => [fmt(v) + ` ${cur()}`, 'مبيعات']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Sales Reps Leaderboard */}
      <RepsLeaderboard
        reps={data?.reps || []}
        loading={loading && !data}
        dateFrom={dateFrom}
        dateTo={dateTo}
      />

      {/* Orders Table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-bold text-foreground">تفاصيل الطلبات</h3>
          <button onClick={exportCSV} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <Download className="w-3.5 h-3.5" /> تصدير
          </button>
        </div>
        {loading && !data ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-xs text-muted-foreground">
                  <tr>
                    <th className="text-right px-4 py-2 font-semibold">الطلب</th>
                    <th className="text-right px-4 py-2 font-semibold">العميل</th>
                    <th className="text-right px-4 py-2 font-semibold">التاريخ</th>
                    <th className="text-right px-4 py-2 font-semibold">المبلغ</th>
                    <th className="text-right px-4 py-2 font-semibold">الحالة</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(data?.orders || data?.items || []).map(o => {
                    const partner = Array.isArray(o.partner_id) ? o.partner_id[1] : (o.partner_name || o.partner || '—');
                    const dateStr = o.date || (o.date_order ? new Date(o.date_order).toLocaleDateString('ar-EG') : '—');
                    const amount = o.amount_total ?? o.amount ?? 0;
                    return (
                      <tr key={o.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 font-mono text-xs text-primary">{o.name}</td>
                        <td className="px-4 py-2.5 font-medium text-xs truncate max-w-32">{partner}</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">{dateStr}</td>
                        <td className="px-4 py-2.5 font-bold text-xs text-secondary whitespace-nowrap">{fmtD(amount)} {cur()}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_COLOR[o.state] || 'bg-muted text-muted-foreground'}`}>
                            {STATUS_MAP[o.state] || o.state}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {!(data?.orders || data?.items)?.length && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">لا توجد طلبات</td></tr>}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => handlePageChange(page - 1)} className="rounded-xl">
                  <ChevronRight className="w-4 h-4" /> السابق
                </Button>
                <span className="text-xs text-muted-foreground">صفحة {page + 1} من {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => handlePageChange(page + 1)} className="rounded-xl">
                  التالي <ChevronLeft className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}